import { bool, string } from 'prop-types'
import { type FunctionComponent } from 'react'

import { type InferPropsWithDefaults } from 'shared/utils/propTypes'

/**
 * Enriches the normal product name with a bold mark for the (first occurence) of the query string
 * we use slice to cut and inject the bold mark, as this enables us to retain the original case
 * of the search query
 * also all chars exceeding max amount of 100 (45 on mobile) will be replaced with ...
 */
export const SearchSuggestionName: FunctionComponent<ComponentProps> = ({
  name,
  queryStr: queryStrRaw,
  concat,
}) => {
  let concatLength = 44
  const longestLength = 99
  const queryStr = queryStrRaw || ''
  let displayName = name

  if (concat && displayName.length > concatLength) {
    // check if there is long word without space in the name
    const withLongWord = displayName.split(' ').some(word => word.length > 25)
    // if so, we need to reduce the length of the name so that it fits on one line
    if (withLongWord) {
      concatLength = 35
    }
    displayName = displayName.substring(0, concatLength).concat('…')
  } else {
    displayName = displayName.substring(0, longestLength)
  }

  const startPos = displayName.toLowerCase().indexOf(queryStr)
  if (startPos > -1) {
    const endPos = startPos + queryStr.length

    const prefix = displayName.slice(0, startPos)
    const needle = displayName.slice(startPos, endPos)
    const suffix = displayName.slice(endPos)

    // Wrapping prefix & suffix with span tags to avoid Google Translate issue.
    // Google Translate mutates DOM by wrapping text nodes with <font /> tags
    // and prevent React reconciliation. Workaround is described here
    // https://github.com/facebook/react/issues/11538#issuecomment-390386520
    return (
      <span className="u-color--blue o-SearchBox__top-searches__list-item">
        {prefix !== '' && <span>{prefix}</span>}
        <span className="u-color--dark u-font-weight--normal o-SearchBox__highlighted-text">
          {needle}
        </span>
        {suffix !== '' && <span>{suffix}</span>}
      </span>
    )
  }

  return <span>{displayName}</span>
}

const propTypes = {
  name: string.isRequired,
  queryStr: string,
  concat: bool,
}

const defaultProps = {
  queryStr: '',
  concat: false,
}

type ComponentProps = InferPropsWithDefaults<
  typeof propTypes,
  typeof defaultProps
>

export default SearchSuggestionName
